import React, { useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkle, Check, Spinner, Warning, ArrowRight, Pause, CaretDown, CaretUp,
  ArrowClockwise,
} from "@phosphor-icons/react";

/**
 * KitProgressBanner — iter v28 r307, sistemato in iter v32 r31
 *
 * Mostra in cima a PropertyDetail lo stato dei contenuti che Pronto sta
 * preparando dopo "Crea annuncio". Risolve il feedback «...e le cose che ho
 * selezionato dove me le crea?».
 *
 * iter v32 r31 — User M1414: «se clicco non mi porta alla sezione, o lo
 * eliminiamo o lo sistemiamo». Adesso ogni riga È cliccabile (quando pronta
 * o quando in attesa) e tramite onJobClick(job) il PropertyDetail naviga al
 * tab giusto. Fixato anche il typo "prontoi"→"pronti".
 *
 * Stati job mappati:
 *   queued / processing → spinner "in lavorazione"
 *   awaiting_review     → "in attesa che approvi le foto"
 *   ready               → check verde
 *   pending / error     → warning con messaggio
 *
 * Polling ogni 5s finché ci sono job non-finali. Quando tutti i job sono
 * `ready` (o non ce ne sono di più), il banner si auto-collassa con un
 * piccolo "Tutti i contenuti sono pronti".
 */
// iter v33 r3 — Allineato con backend kit_worker stati reali:
//   "ready" / "error" / "skipped" → FINAL (lavoro concluso)
//   "queued" / "processing" / "pending" / "awaiting_review" → ANCORA IN CORSO
// Prima "pending" era FINAL, ma con il fix v33 r3 lo step `enhance_photos`
// resta "pending" finché TUTTE le foto non sono migliorate. Senza questa
// modifica il banner mostrava "✓ Pronto" mentre c'erano ancora 7/8 da fare.
const FINAL_STATES = new Set(["ready", "error", "skipped"]);

export default function KitProgressBanner({ propertyId, onReviewClick, onJobClick }) {
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [restarting, setRestarting] = useState(false);
  // iter v33 r14 · Never-regress ref per il progress (evita che la barra
  // torni indietro se il backend ri-emette temporaneamente count=0).
  const maxSeenRef = useRef({ done: 0, total: 0 });

  const fetchStatus = useCallback(async () => {
    if (!propertyId) return;
    try {
      const { data: r } = await api.get(`/properties/${propertyId}/kit-jobs`);
      setData(r);
    } catch (e) {
      // 404 → nessun sopralluogo collegato, nascondi il banner
      setHidden(true);
    }
  }, [propertyId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Polling automatico finché ci sono job in lavorazione
  useEffect(() => {
    const jobs = data?.jobs || [];
    const stillWorking = jobs.some(
      (j) => !FINAL_STATES.has(j.status) && j.status !== "awaiting_review",
    );
    if (!stillWorking) return;
    const t = setInterval(fetchStatus, 5000);
    return () => clearInterval(t);
  }, [data, fetchStatus]);

  // iter v33 r10 · Retry manuale — rilancia il worker sui job appesi
  // iter v33 r11 · Ora skippa anche i vecchi Wave 2 (post_social, ecc.).
  // Toast rispecchia la doppia azione: foto riavviate + contenuti extra
  // "chiusi" (l'utente può crearli a mano quando serve).
  const handleRestart = useCallback(async () => {
    if (!propertyId || restarting) return;
    setRestarting(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/kit-jobs/retry`);
      const requeued = data?.requeued || 0;
      const skipped = data?.skipped_extras || 0;
      if (requeued > 0 && skipped > 0) {
        toast.success(
          "Foto rimesse in lavorazione",
          { description: `Post social e materiali extra li crei tu dalle sezioni dedicate (${skipped} rimossi).` },
        );
      } else if (requeued > 0) {
        toast.success("Foto rimesse in lavorazione · può richiedere qualche minuto");
      } else if (skipped > 0) {
        toast.success(
          "Contenuti extra rimossi",
          { description: "Li crei tu dalle sezioni Sul tuo sito / Post per social." },
        );
      } else {
        toast.info("Nessun contenuto da riavviare");
      }
      // Ri-fetch subito così l'utente vede lo stato aggiornato
      await fetchStatus();
    } catch (e) {
      toast.error("Non è stato possibile riavviare · riprova tra qualche secondo");
    } finally {
      setRestarting(false);
    }
  }, [propertyId, restarting, fetchStatus]);

  if (hidden) return null;
  const jobs = data?.jobs || [];
  if (jobs.length === 0) return null;

  const reviewPending = data?.review_status === "pending";
  const hasStuckJobs = !!data?.has_stuck_jobs;
  const totalReady = jobs.filter((j) => j.status === "ready").length;
  const totalAwaitingReview = jobs.filter((j) => j.status === "awaiting_review").length;
  const totalWorking = jobs.filter(
    (j) => !FINAL_STATES.has(j.status) && j.status !== "awaiting_review",
  ).length;
  const totalErrors = jobs.filter((j) => j.status === "error").length;

  const allReady = totalReady === jobs.length && jobs.length > 0;

  // iter v33 r12 · User M1490: «più compatto, tipo mini-chip in cima».
  // Modalità COMPATTA: mostra un chip slim quando c'è 1 solo job in
  // lavorazione (tipico del nuovo sopralluogo semplificato), senza
  // stuck/awaiting/errori. La card grande resta per stuck/multi-job.
  const workingJob = jobs.find(
    (j) => !FINAL_STATES.has(j.status) && j.status !== "awaiting_review",
  );
  const isCompactMode = (
    !hasStuckJobs
    && totalAwaitingReview === 0
    && totalErrors === 0
    && !allReady
    && totalWorking === 1
    && !!workingJob
  );

  if (isCompactMode) {
    const isEnhance = workingJob.kind === "enhance_photos";
    const done = workingJob.count || 0;
    const total = workingJob.total || 0;
    // Aggiorno il MAX visto (never-regress). useRef è al top-level della
    // componente, qui aggiorno solo il valore corrente.
    if (done > maxSeenRef.current.done) maxSeenRef.current.done = done;
    if (total > maxSeenRef.current.total) maxSeenRef.current.total = total;
    const effDone = maxSeenRef.current.done;
    const effTotal = maxSeenRef.current.total;
    const pct = effTotal > 0 ? Math.round((effDone / effTotal) * 100) : null;

    // iter v33 r14 · User M1492 «metti quanto manca». ETA basata sul tempo
    // trascorso dal created_at del job e sulle foto già pronte.
    let etaLabel = null;
    if (isEnhance && effTotal > 0) {
      const remaining = Math.max(0, effTotal - effDone);
      if (remaining > 0) {
        let etaSec;
        const startTs = workingJob.created_at
          ? new Date(String(workingJob.created_at).replace(" ", "T")).getTime()
          : null;
        const elapsedSec = startTs ? (Date.now() - startTs) / 1000 : 0;
        if (effDone > 0 && elapsedSec > 4) {
          etaSec = Math.round((elapsedSec / effDone) * remaining);
        } else {
          // iter v33 r18 · User M1509. Stima realistica dopo test e2e:
          // batch di 3 foto in parallelo, ~75-80s a batch reali (Nano
          // Banana ha variance alta). Prima 60s era troppo ottimistico.
          etaSec = Math.ceil(remaining / 3) * 80;
        }
        if (etaSec < 60) {
          etaLabel = `~${etaSec}s rimanenti`;
        } else {
          const m = Math.round(etaSec / 60);
          etaLabel = `~${m} minut${m === 1 ? "o" : "i"} rimanenti`;
        }
      }
    }

    return (
      <button
        type="button"
        onClick={() => onJobClick?.(workingJob)}
        className="w-full mb-4 flex items-center gap-3 pl-3 pr-4 py-2.5 rounded-full bg-[#F1F4ED] border border-[#7C8B6E]/25 hover:border-[#7C8B6E]/50 hover:bg-[#E8EDDF] transition-colors text-left group"
        data-testid="kit-progress-chip"
      >
        <div className="shrink-0 w-8 h-8 rounded-full bg-white border border-[#7C8B6E]/25 flex items-center justify-center">
          <Sparkle size={14} weight="fill" className="text-[#7C8B6E] animate-pulse" />
        </div>
        <div className="flex-1 min-w-0 text-center">
          <div className="text-[13px] font-semibold text-[#1C1C1A] leading-tight truncate">
            {isEnhance
              ? (effTotal > 0 ? `Foto in lavorazione · ${effDone}/${effTotal}` : "Foto in lavorazione")
              : (workingJob.label || "Contenuto in lavorazione")}
          </div>
          {isEnhance && (
            <>
              <div className="mt-1.5 h-1 rounded-full bg-white/70 overflow-hidden">
                <div
                  className="h-full bg-[#7C8B6E] transition-all duration-500 ease-out"
                  style={{ width: `${pct ?? 8}%` }}
                  data-testid="kit-progress-chip-bar"
                />
              </div>
              {etaLabel && (
                <div className="text-[10.5px] leading-tight mt-1 text-[#6B6A65]" data-testid="kit-progress-chip-eta">
                  {etaLabel}
                </div>
              )}
            </>
          )}
        </div>
        <div className="shrink-0 text-[10px] uppercase tracking-[0.14em] text-[#7C8B6E] font-bold group-hover:translate-x-0.5 transition-transform hidden sm:block">
          Dettagli
        </div>
      </button>
    );
  }

  return (
    <div
      className="mb-4 rounded-2xl border border-[#1C1C1A]/10 bg-white overflow-hidden shadow-sm"
      data-testid="kit-progress-banner"
    >
      {/* Header */}
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="w-full flex items-center gap-3 px-4 py-3 sm:px-5 sm:py-4 hover:bg-[#FAF8F3] transition-colors text-left"
        data-testid="kit-progress-toggle"
      >
        <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
          allReady ? "bg-[#7C8B6E] text-white"
          : reviewPending ? "bg-amber-500 text-white"
          : "bg-[#1C1C1A]/5 text-[#1C1C1A]"
        }`}>
          {allReady
            ? <Check size={18} weight="bold" />
            : reviewPending
              ? <Pause size={18} weight="fill" />
              : <Sparkle size={18} weight="fill" className={totalWorking > 0 ? "animate-pulse" : ""} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-[#1C1C1A]">
            {allReady
              ? "Tutto pronto · contenuti del sopralluogo"
              : reviewPending
                ? "Pronto sta sistemando le foto…"
                : totalWorking > 0
                  ? `Pronto sta preparando ${totalWorking} ${totalWorking === 1 ? "contenuto" : "contenuti"}…`
                  : "Contenuti del sopralluogo"}
          </div>
          <div className="text-[12px] text-[#6B6A65] mt-0.5 flex flex-wrap gap-x-3">
            {totalReady > 0 && (
              <span className="text-[#5E7048] font-medium">
                ✓ {totalReady} pront{totalReady === 1 ? "o" : "i"}
              </span>
            )}
            {totalWorking > 0 && (
              <span>⟳ {totalWorking} in lavorazione</span>
            )}
            {totalAwaitingReview > 0 && (
              <span className="text-amber-700 font-medium">⏸ {totalAwaitingReview} in attesa</span>
            )}
            {totalErrors > 0 && (
              <span className="text-red-700">! {totalErrors} con errore</span>
            )}
          </div>
        </div>
        {collapsed ? <CaretDown size={18} weight="bold" className="text-[#6B6A65]" /> : <CaretUp size={18} weight="bold" className="text-[#6B6A65]" />}
      </button>

      {/* Body — lista job */}
      {!collapsed && (
        <div className="px-4 pb-4 sm:px-5 sm:pb-5 space-y-2 border-t border-[#1C1C1A]/5 pt-3">
          {jobs.map((j) => (
            <JobRow
              key={j.id}
              job={j}
              onReviewClick={onReviewClick}
              onJobClick={onJobClick}
            />
          ))}
          {allReady && (
            <div className="pt-2 text-[12px] text-[#6B6A65] italic">
              Tocca una riga per andare a vedere il contenuto pronto.
            </div>
          )}
          {/* iter v33 r10 · User M1487 — CTA di "sblocco" quando il backend
              rileva che il worker si è fermato senza completare i job. */}
          {hasStuckJobs && (
            <div
              className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-3"
              data-testid="kit-stuck-cta"
            >
              <Warning size={18} weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-amber-900 leading-tight">
                  Lavorazione interrotta
                </div>
                <div className="text-[12px] text-amber-800 mt-0.5 leading-snug">
                  La preparazione dei contenuti si è fermata prima del previsto.
                  Un tap qui sotto e Pronto riprova da dove era rimasto.
                </div>
                <button
                  type="button"
                  onClick={handleRestart}
                  disabled={restarting}
                  className="mt-2.5 inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white text-[12.5px] font-semibold px-3 py-1.5 rounded-full transition-colors"
                  data-testid="kit-stuck-restart"
                >
                  {restarting ? (
                    <><Spinner size={13} className="animate-spin" /> Riavvio…</>
                  ) : (
                    <><ArrowClockwise size={13} weight="bold" /> Riavvia lavorazione</>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function JobRow({ job, onReviewClick, onJobClick }) {
  const { kind, label, status, note } = job;
  const labelText = label || kind;

  const isReady = status === "ready";
  const isError = status === "error";
  const isAwaitingReview = status === "awaiting_review";
  const isPending = status === "pending";
  const isWorking = !FINAL_STATES.has(status) && !isAwaitingReview;

  let StatusIcon = Spinner;
  let iconCls = "text-[#6B6A65] animate-spin";
  let statusText = "In lavorazione";

  if (isReady) {
    StatusIcon = Check;
    iconCls = "text-[#7C8B6E]";
    statusText = "Pronto · tocca per vedere";
  } else if (isError) {
    StatusIcon = Warning;
    iconCls = "text-red-600";
    statusText = note || "Errore";
  } else if (isAwaitingReview) {
    StatusIcon = Pause;
    iconCls = "text-amber-600";
    statusText = "In attesa che approvi le foto";
  } else if (isPending) {
    // iter v33 r3 — Backend ora ritorna `note` chiare tipo "3/8 migliorate · 5
    // da riprovare" → mostriamole. Pending NON è più uno stato finale (vedi
    // FINAL_STATES) ma temporary: il banner resta animato.
    StatusIcon = Spinner;
    iconCls = "text-[#6B6A65] animate-spin";
    statusText = note || "In lavorazione…";
  } else if (isWorking) {
    // iter v33 r3 — Se il backend ha mandato una nota di progress (es.
    // "3/8 migliorate"), mostriamola anche in stato working/processing.
    statusText = note || "In lavorazione…";
  }

  // iter v32 r31 — riga cliccabile se è pronta (vai a vedere il contenuto)
  // o in attesa di review (apri review foto).
  // iter v33 r3 — Ora anche le righe "pending" (es. enhance_photos con
  // foto rimaste da migliorare) sono cliccabili → mandano l'utente al
  // tab Foto dove può premere "Migliora tutte" per riprovare le mancanti.
  const clickable =
    ((isReady || isPending) && typeof onJobClick === "function") ||
    (isAwaitingReview && (typeof onReviewClick === "function" || typeof onJobClick === "function"));
  const handleClick = () => {
    if (isAwaitingReview && typeof onReviewClick === "function") {
      onReviewClick(job);
    } else if (typeof onJobClick === "function") {
      onJobClick(job);
    }
  };

  const baseClasses = "w-full flex items-center gap-3 px-3 py-2 rounded-lg bg-[#FAF8F3]/60 text-left";

  if (!clickable) {
    return (
      <div className={baseClasses} data-testid={`kit-progress-row-${kind}`}>
        <StatusIcon size={16} weight={isReady ? "bold" : "fill"} className={`shrink-0 ${iconCls}`} />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium text-[#1C1C1A] truncate">{labelText}</div>
          <div className="text-[11px] text-[#6B6A65] truncate">{statusText}</div>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`${baseClasses} hover:bg-[#F1F4ED] active:scale-[0.99] transition-all`}
      data-testid={`kit-progress-row-${kind}`}
    >
      <StatusIcon size={16} weight={isReady ? "bold" : "fill"} className={`shrink-0 ${iconCls}`} />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-[#1C1C1A] truncate">{labelText}</div>
        <div className="text-[11px] text-[#6B6A65] truncate">{statusText}</div>
      </div>
      {isAwaitingReview ? (
        <span
          className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-[#1C1C1A] text-white text-[11px] font-semibold"
          data-testid={`kit-progress-review-cta-${kind}`}
        >
          Rivedi <ArrowRight size={11} weight="bold" />
        </span>
      ) : (
        <ArrowRight size={14} weight="bold" className="shrink-0 text-[#9A968B]" />
      )}
    </button>
  );
}
